import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Separator } from "../components/ui/separator";
import { ArrowLeft, FileText, Download, Save, Send } from "lucide-react";

const FENCE_TYPES = [
  { value: "wood", label: "Wood (Pressure Treated)" },
  { value: "cedar", label: "Cedar" },
  { value: "chain_link", label: "Chain Link" },
  { value: "ornamental_iron", label: "Ornamental Iron" },
  { value: "vinyl", label: "Vinyl" },
];

const POST_TYPES = [
  { value: "4x4", label: "4x4 Standard" },
  { value: "4x6", label: "4x6 Heavy Duty" },
  { value: "6x6", label: "6x6 Extra Heavy" },
  { value: "steel_pipe", label: "Cladded Steel Pipe" },
];

// Calgary 2026 pricing presets (CAD) - combined labor + material per ft
const PRICING_PRESETS = {
  wood: {
    "4x4": { labor: 28, material: 30, gate: 450 },      // $58/ft total
    "4x6": { labor: 28, material: 33, gate: 450 },      // $61/ft total
    "6x6": { labor: 28, material: 35, gate: 450 },      // $63/ft total
    "steel_pipe": { labor: 28, material: 37, gate: 450 }, // $65/ft total
  },
  cedar: {
    "4x4": { labor: 30, material: 38, gate: 500 },
    "4x6": { labor: 30, material: 41, gate: 500 },
    "6x6": { labor: 30, material: 43, gate: 500 },
    "steel_pipe": { labor: 30, material: 45, gate: 500 },
  },
  chain_link: { labor: 20, material: 16, gate: 350 },
  ornamental_iron: { labor: 35, material: 50, gate: 600 },
  vinyl: { labor: 38, material: 38, gate: 550 },
};

function getPricing(fenceType, postType) {
  const preset = PRICING_PRESETS[fenceType];
  if (!preset) return { labor: 28, material: 30, gate: 450 };
  // Wood and cedar have post-type-specific pricing
  if (preset[postType]) return preset[postType];
  // Flat pricing for other types
  if (preset.labor !== undefined) return preset;
  // Default to 4x4 if post-specific pricing exists but post not found
  return preset["4x4"] || { labor: 28, material: 30, gate: 450 };
}

export default function EstimateBuilderPage() {
  const [searchParams] = useSearchParams();
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const leadId = searchParams.get("lead_id");
  const [lead, setLead] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    fence_type: "wood",
    linear_footage: 100,
    height: 6,
    num_gates: 1,
    tear_out: false,
    post_type: "4x4",
    labor_cost_per_foot: 28,
    material_cost_per_foot: 30,
    gate_cost: 450,
    tear_out_cost: 8,
    tax_rate: 5,
    notes: "",
  });

  useEffect(() => {
    if (leadId) api.get(`/leads/${leadId}`).then(r => setLead(r.data)).catch(err => console.error("Failed to load lead:", err));
    if (estimateId) api.get(`/estimates/${estimateId}`).then(r => { setForm(r.data); setLead(null); if (r.data.lead_id) api.get(`/leads/${r.data.lead_id}`).then(lr => setLead(lr.data)).catch(err => console.error("Failed to load lead:", err)); }).catch(err => console.error("Failed to load estimate:", err));
  }, [leadId, estimateId]);

  // Auto-update Calgary pricing when fence type or post type changes
  const handleFenceTypeChange = (ftype) => {
    const pricing = getPricing(ftype, form.post_type);
    setForm(prev => ({
      ...prev,
      fence_type: ftype,
      labor_cost_per_foot: pricing.labor,
      material_cost_per_foot: pricing.material,
      gate_cost: pricing.gate,
    }));
  };

  const handlePostTypeChange = (ptype) => {
    const pricing = getPricing(form.fence_type, ptype);
    setForm(prev => ({
      ...prev,
      post_type: ptype,
      labor_cost_per_foot: pricing.labor,
      material_cost_per_foot: pricing.material,
      gate_cost: pricing.gate,
    }));
  };

  const calc = () => {
    const labor = form.labor_cost_per_foot * form.linear_footage;
    const material = form.material_cost_per_foot * form.linear_footage;
    const gates = form.gate_cost * form.num_gates;
    const tearOut = form.tear_out ? form.tear_out_cost * form.linear_footage : 0;
    const subtotal = labor + material + gates + tearOut;
    const tax = subtotal * (form.tax_rate / 100);
    return { labor, material, gates, tearOut, subtotal, tax, total: subtotal + tax };
  };

  const totals = calc();

  const handleSave = async () => {
    setSaving(true);
    try {
      if (estimateId) {
        await api.put(`/estimates/${estimateId}`, form);
        toast.success("Estimate updated");
      } else {
        const { data } = await api.post("/estimates", { ...form, lead_id: leadId || "" });
        toast.success("Estimate created");
        navigate(`/estimates/${data.id}`);
      }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDownloadPdf = async () => {
    if (!estimateId) { toast.error("Save estimate first"); return; }
    try {
      const response = await api.get(`/estimates/${estimateId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estimate_${estimateId.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Failed to download PDF"); }
  };

  const [sending, setSending] = useState(false);
  const handleSendToCustomer = async () => {
    if (!estimateId) { toast.error("Save estimate first"); return; }
    if (!leadId && !form.lead_id) { toast.error("No customer linked to this estimate"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/estimates/send-to-customer", { estimate_id: estimateId });
      if (data.success) {
        toast.success("Estimate sent to customer via SMS!");
      } else {
        toast.error(`SMS failed: ${data.sms_result?.error || "Unknown error"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="estimate-builder-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="text-xl sm:text-2xl font-extrabold text-zinc-900">
          {estimateId ? "Edit Estimate" : "New Estimate"}
        </h1>
      </div>

      {lead && (
        <Card className="border border-zinc-200 shadow-none bg-orange-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-orange-600" />
            <div>
              <p className="text-sm font-semibold text-zinc-900">{lead.name}</p>
              <p className="text-xs text-zinc-500">{lead.address || lead.phone || lead.email}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-3 space-y-4">
          <Card className="border border-zinc-200 shadow-none">
            <CardHeader className="pb-3"><CardTitle className="text-base font-bold">Fence Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Fence Type</Label>
                  <Select value={form.fence_type} onValueChange={handleFenceTypeChange}>
                    <SelectTrigger data-testid="est-fence-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{FENCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-400">${form.labor_cost_per_foot + form.material_cost_per_foot}/ft total - Calgary 2026 pricing auto-applied</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Post Type</Label>
                  <Select value={form.post_type} onValueChange={handlePostTypeChange}>
                    <SelectTrigger data-testid="est-post-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{POST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Linear Footage</Label>
                  <Input type="number" value={form.linear_footage} onChange={(e) => updateForm("linear_footage", parseFloat(e.target.value) || 0)} data-testid="est-footage" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Height (ft)</Label>
                  <Input type="number" value={form.height} onChange={(e) => updateForm("height", parseFloat(e.target.value) || 0)} data-testid="est-height" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Number of Gates</Label>
                  <Input type="number" value={form.num_gates} onChange={(e) => updateForm("num_gates", parseInt(e.target.value) || 0)} data-testid="est-gates" />
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <Checkbox checked={form.tear_out} onCheckedChange={(v) => updateForm("tear_out", v)} id="tear_out" data-testid="est-tearout" />
                    <Label htmlFor="tear_out" className="text-sm font-medium cursor-pointer">Tear Out Required</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-zinc-200 shadow-none">
            <CardHeader className="pb-3"><CardTitle className="text-base font-bold">Pricing</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Labor $/ft</Label>
                  <Input type="number" step="0.01" value={form.labor_cost_per_foot} onChange={(e) => updateForm("labor_cost_per_foot", parseFloat(e.target.value) || 0)} data-testid="est-labor-cost" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Material $/ft</Label>
                  <Input type="number" step="0.01" value={form.material_cost_per_foot} onChange={(e) => updateForm("material_cost_per_foot", parseFloat(e.target.value) || 0)} data-testid="est-material-cost" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Gate Cost ($)</Label>
                  <Input type="number" step="0.01" value={form.gate_cost} onChange={(e) => updateForm("gate_cost", parseFloat(e.target.value) || 0)} data-testid="est-gate-cost" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tear Out $/ft</Label>
                  <Input type="number" step="0.01" value={form.tear_out_cost} onChange={(e) => updateForm("tear_out_cost", parseFloat(e.target.value) || 0)} data-testid="est-tearout-cost" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tax Rate (%)</Label>
                  <Input type="number" step="0.1" value={form.tax_rate} onChange={(e) => updateForm("tax_rate", parseFloat(e.target.value) || 0)} data-testid="est-tax-rate" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} placeholder="Additional notes..." rows={3} data-testid="est-notes" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Totals */}
        <div className="lg:col-span-2">
          <Card className="border border-zinc-200 shadow-none sticky top-4">
            <CardHeader className="pb-3"><CardTitle className="text-base font-bold">Estimate Summary</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Labor</span><span className="text-zinc-900">${totals.labor.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Materials</span><span className="text-zinc-900">${totals.material.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                {form.num_gates > 0 && <div className="flex justify-between"><span className="text-zinc-500">Gates ({form.num_gates})</span><span className="text-zinc-900">${totals.gates.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>}
                {form.tear_out && <div className="flex justify-between"><span className="text-zinc-500">Tear Out</span><span className="text-zinc-900">${totals.tearOut.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>}
                <Separator />
                <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-medium text-zinc-900">${totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Tax ({form.tax_rate}%)</span><span className="text-zinc-900">${totals.tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <Separator />
                <div className="flex justify-between text-lg font-extrabold">
                  <span className="text-zinc-900">Total</span>
                  <span className="text-orange-600" data-testid="est-total">${totals.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <div className="space-y-2 pt-4">
                <Button onClick={handleSave} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-estimate-btn">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Estimate"}
                </Button>
                {estimateId && (
                  <>
                    <Button onClick={handleSendToCustomer} disabled={sending} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-to-customer-btn">
                      <Send className="w-4 h-4 mr-2" /> {sending ? "Sending..." : "Send to Customer via SMS"}
                    </Button>
                    <Button onClick={handleDownloadPdf} variant="outline" className="w-full" data-testid="download-pdf-btn">
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
